import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  Button,
  Carousel,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  TextInput,
  Textarea,
} from "flowbite-react";

import { auctionPropertiesThunks } from "../../app/store";
import { http } from "../../api/http";
import { API_ORIGIN } from "../../api/http";
import { uploadPropertyMedia, buildFileFromClipboard } from "../../utils/propertyMediaUpload";

console.log(
  "URL FOTOS > REACT_APP_API_BASE_URL",
  process.env.REACT_APP_API_BASE_URL,
);

const MEDIA_TYPE_UPLOAD = "PHOTO_STEP7";

function toAbsoluteUrl(url) {
  if (!url) return "";

  // já é absoluta
  if (url.startsWith("http://") || url.startsWith("https://")) return url;

  // se vier "/media/..." vira "http://localhost:5050/media/..." (dev)
  // ou "https://taxdeeddesk.com/media/..." (prod)
  if (url.startsWith("/")) {
    // se API_ORIGIN estiver vazio (fallback), mantém relativo
    return API_ORIGIN ? `${API_ORIGIN}${url}` : url;
  }

  return API_ORIGIN ? `${API_ORIGIN}/${url}` : url;
}

function money(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export default function PropertyCardModal({ open, onClose, propertyId }) {
  const dispatch = useDispatch();
  const userId = useSelector((s) => s.auth?.user?.id);

  // ✅ aqui a gente pega do store (pós getById)
  const property = useSelector((s) => {
    const current = s.auctionProperties?.current || null;
    const byId = propertyId ? s.auctionProperties?.byId?.[propertyId] : null;

    // se current for exatamente a property aberta, usa ele
    if (current?.id && propertyId && current.id === propertyId) return current;

    // senão tenta byId
    return byId || current || null;
  });

  const [loading, setLoading] = useState(false);

  // upload state
  const uploadingRef = useRef(false);
  const [labelBase, setLabelBase] = useState("step7");
  const [queue, setQueue] = useState([]); // { id, file, previewUrl, label }
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const pasteBoxRef = useRef(null);

  useEffect(() => {
    if (!open || !propertyId) return;

    setLoading(true);
    dispatch(auctionPropertiesThunks.getById(propertyId)).finally(() =>
      setLoading(false),
    );
  }, [open, propertyId, dispatch]);

  // cleanup previews
  useEffect(() => {
    return () => {
      queue.forEach((q) => {
        try {
          URL.revokeObjectURL(q.previewUrl);
        } catch {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // paste handler
  useEffect(() => {
    function onPaste(e) {
      if (!pasteBoxRef.current) return;

      const within = pasteBoxRef.current.contains(e.target);
      if (!within) return;

      const items = e.clipboardData?.items || [];
      const images = [];
      for (const it of items) {
        if (it.type?.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) images.push(f);
        }
      }
      if (!images.length) return;

      e.preventDefault();

      const stamped = images.map((f, idx) =>
        buildFileFromClipboard(f, labelBase, idx, "photo"),
      );

      addFilesToQueue(stamped, true);
    }

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [labelBase]);

  function addFilesToQueue(files, fromClipboard = false) {
    const base = (labelBase || "step7").trim();
    const next = files.map((file, idx) => {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}-${idx}`;
      const previewUrl = URL.createObjectURL(file);
      const cleanBase = base.replace(/\s+/g, " ").trim();
      const label = fromClipboard
        ? `${cleanBase} (paste ${idx + 1})`
        : `${cleanBase} (${idx + 1})`;
      return { id, file, previewUrl, label };
    });
    setQueue((prev) => [...next, ...prev]);
  }

  function onFileInputChange(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    addFilesToQueue(files, false);
  }

  function removeFromQueue(id) {
    setQueue((prev) => {
      const item = prev.find((x) => x.id === id);
      if (item?.previewUrl) {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {}
      }
      return prev.filter((x) => x.id !== id);
    });
  }

  function updateQueueLabel(id, label) {
    setQueue((prev) => prev.map((x) => (x.id === id ? { ...x, label } : x)));
  }

  async function uploadQueue() {
    if (!propertyId) return;
    if (uploadingRef.current) return;
    uploadingRef.current = true;

    setUploadError(null);

    if (!userId) {
      setUploadError("Não foi possível identificar o usuário logado.");
      uploadingRef.current = false;
      return;
    }

    if (!queue.length) {
      uploadingRef.current = false;
      return;
    }

    setUploading(true);
    try {
      for (const item of queue) {
        const r = await uploadPropertyMedia({
          file: item.file,
          auction_property_id: propertyId,
          media_type: MEDIA_TYPE_UPLOAD,
          label: item.label,
          uploaded_by_user_id: userId,
        });

        if (!r?.success) throw new Error(r?.error || "Falha no upload");
      }

      // limpa fila
      queue.forEach((q) => {
        try {
          URL.revokeObjectURL(q.previewUrl);
        } catch {}
      });
      setQueue([]);

      // ✅ refresh property
      await dispatch(auctionPropertiesThunks.getById(propertyId));
    } catch (err) {
      setUploadError(err?.message || "Erro no upload");
    } finally {
      setUploading(false);
      uploadingRef.current = false;
    }
  }

  const allMedia = useMemo(() => {
    const m = property?.media || property?.PropertyMedia || [];
    return [...m]
      .filter((x) => x?.url)
      .sort(
        (a, b) =>
          new Date(b.created_at || b.createdAt || 0) -
          new Date(a.created_at || a.createdAt || 0),
      );
  }, [property]);

  const liens = useMemo(() => {
    const l = property?.liens || property?.PropertyLiens || [];
    return [...l].sort(
      (a, b) =>
        new Date(b.recorded_at || b.recordedAt || "1970-01-01") -
        new Date(a.recorded_at || a.recordedAt || "1970-01-01"),
    );
  }, [property]);

  const liensSummary = useMemo(() => {
    const relevant = liens.filter(
      (x) => x.status === "OPEN" || x.status === "UNKNOWN",
    );
    const sum = relevant.reduce((acc, x) => acc + (Number(x.amount) || 0), 0);
    return {
      openUnknownCount: relevant.length,
      openUnknownSum: sum,
      totalCount: liens.length,
    };
  }, [liens]);

  function ratingBadge(rating) {
    if (!rating) return <Badge color="warning">Pendente</Badge>;
    const color =
      rating === "A" ? "success" : rating === "B" ? "info" : rating === "REDEEMED" ? "warning" : "failure";
    return <Badge color={color}>Rating {rating}</Badge>;
  }

  return (
    <Modal show={open} onClose={onClose} size="7xl">
      <ModalHeader>Property Card (Read-only)</ModalHeader>

      <ModalBody>
        {loading || !property ? (
          <div className="p-6 flex items-center gap-3">
            <Spinner />
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Carregando detalhes...
            </span>
          </div>
        ) : (
          <div className="space-y-5">
            {/* HEADER */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div className="space-y-1">
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {property.property_address || "—"}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    <span className="font-medium">Parcel:</span>{" "}
                    {property.parcel_number || "—"}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    <span className="font-medium">Owner:</span>{" "}
                    {property.owner_name || "—"}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {ratingBadge(property.rating)}
                  <Badge color="gray">
                    {property.homestead_investor || "—"}
                  </Badge>
                  <Badge color="gray">{property.type_lot || "—"}</Badge>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
                  <div className="text-xs text-gray-500">Min Bid</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {money(property.min_bid)}
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
                  <div className="text-xs text-gray-500">Appraisal Value</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {money(property.appraisal_value)}
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
                  <div className="text-xs text-gray-500">Market Value</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {money(property.total_value)}
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
                  <div className="text-xs text-gray-500">
                    Liens (Open/Unknown)
                  </div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {money(liensSummary.openUnknownSum)}{" "}
                    <span className="text-xs text-gray-500">
                      ({liensSummary.openUnknownCount}/{liensSummary.totalCount}
                      )
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* CAROUSEL */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Fotos e Prints (todas as mídias)
                </div>
                <div className="text-xs text-gray-500">
                  {allMedia.length} arquivo(s)
                </div>
              </div>

              {allMedia.length ? (
                <div className="mt-3 h-80">
                  <Carousel slideInterval={6000}>
                    {allMedia.map((m) => (
                      <div key={m.id} className="relative h-full w-full">
                        <img
                          src={toAbsoluteUrl(m.url)}
                          alt={m.label || m.media_type || "media"}
                          className="h-full w-full object-contain bg-gray-50 dark:bg-gray-950 rounded-lg"
                        />
                        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2">
                          <div className="truncate text-xs px-2 py-1 rounded bg-black/50 text-white">
                            <span className="font-semibold">
                              {m.media_type}
                            </span>
                            {m.label ? ` — ${m.label}` : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                  </Carousel>
                </div>
              ) : (
                <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                  Nenhuma mídia anexada ainda.
                </div>
              )}
            </div>

            {/* UPLOAD STEP7 */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Adicionar fotos (Step 7)
                  </div>
                  <div className="text-xs text-gray-500">
                    Única ação permitida aqui: upload de imagens do tipo{" "}
                    <span className="font-mono">{MEDIA_TYPE_UPLOAD}</span>.
                  </div>
                </div>

                <div className="min-w-[180px]">
                  <Label>Nome base</Label>
                  <TextInput
                    value={labelBase}
                    onChange={(e) => setLabelBase(e.target.value)}
                    placeholder="Ex: site-photo"
                  />
                </div>
              </div>

              <div
                ref={pasteBoxRef}
                tabIndex={0}
                className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/30"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Cole imagens aqui (Ctrl+V)
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Clique nesta caixa e cole. Você pode revisar antes de enviar.
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <label className="inline-flex">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={onFileInputChange}
                      disabled={uploading}
                    />
                    <Button color="info" as="span" disabled={uploading}>
                      Selecionar imagens do PC
                    </Button>
                  </label>

                  <Button
                    className="text-slate-800"
                    onClick={uploadQueue}
                    disabled={uploading || queue.length === 0}
                  >
                    {uploading ? (
                      <span className="inline-flex items-center gap-2">
                        <Spinner size="sm" /> Enviando...
                      </span>
                    ) : (
                      `Enviar ${queue.length} imagem(ns)`
                    )}
                  </Button>
                </div>

                {uploadError && (
                  <div className="mt-3 text-sm text-red-600">{uploadError}</div>
                )}
              </div>

              {queue.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {queue.map((q) => (
                    <div
                      key={q.id}
                      className="border border-gray-200 dark:border-gray-800 rounded-lg p-3 bg-white dark:bg-gray-900"
                    >
                      <img
                        src={q.previewUrl}
                        alt={q.label || "preview"}
                        className="w-full h-40 object-cover rounded-md"
                      />
                      <div className="mt-2 space-y-2">
                        <div>
                          <Label>Label</Label>
                          <TextInput
                            value={q.label}
                            onChange={(e) =>
                              updateQueueLabel(q.id, e.target.value)
                            }
                          />
                        </div>
                        <div className="flex justify-end">
                          <Button
                            color="failure"
                            size="xs"
                            onClick={() => removeFromQueue(q.id)}
                          >
                            Remover
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* DETAILS (read-only) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left */}
              <div className="space-y-4">
                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Utilities / Regras
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">POA/HOA:</span>{" "}
                      {property.poa_hoa || "—"}
                    </div>
                    <div>
                      <span className="text-gray-500">Zoning:</span>{" "}
                      {property.zoning_code || "—"}
                    </div>
                    <div>
                      <span className="text-gray-500">Electric:</span>{" "}
                      {property.electric || "—"}
                    </div>
                    <div>
                      <span className="text-gray-500">Water:</span>{" "}
                      {property.water || "—"}
                    </div>
                    <div>
                      <span className="text-gray-500">Sewer:</span>{" "}
                      {property.sewer || "—"}
                    </div>
                    <div>
                      <span className="text-gray-500">Lot Area:</span>{" "}
                      {property.lot_area ?? "—"}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Google Maps
                  </div>
                  <div className="mt-2 text-sm">
                    <div className="text-gray-500">Contexto</div>
                    <div className="whitespace-pre-wrap text-gray-900 dark:text-white">
                      {property.contexto || "—"}
                    </div>
                    <div className="mt-2 text-gray-500">Relevo</div>
                    <div className="text-gray-900 dark:text-white">
                      {property.relevo || "—"}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    FEMA
                  </div>
                  <div className="mt-2 text-sm">
                    <div>
                      <span className="text-gray-500">Zone Code:</span>{" "}
                      {property.fema_zone_code || "—"}
                    </div>
                    <div className="mt-2 text-gray-500">Risk Notes</div>
                    <div className="whitespace-pre-wrap text-gray-900 dark:text-white">
                      {property.fema_risk_notes || "—"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right */}
              <div className="space-y-4">
                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Zillow
                  </div>
                  <div className="mt-2 text-sm space-y-2">
                    <div>
                      <div className="text-gray-500">Zillow URL</div>
                      {property.zillow_url ? (
                        <a
                          href={property.zillow_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 dark:text-blue-400 break-all"
                        >
                          {property.zillow_url}
                        </a>
                      ) : (
                        <div className="text-gray-900 dark:text-white">—</div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-gray-500">Market Value</div>
                        <div className="text-gray-900 dark:text-white font-semibold">
                          {money(property.total_value)}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Sold USD / sqft</div>
                        <div className="text-gray-900 dark:text-white font-semibold">
                          {property.sold_usd_per_sqft ?? "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Liens
                  </div>

                  {liens.length ? (
                    <div className="mt-3 space-y-2">
                      <div className="text-xs text-gray-500">
                        {liensSummary.totalCount} lien(s) • Open/Unknown:{" "}
                        {money(liensSummary.openUnknownSum)}
                      </div>

                      <div className="space-y-2 max-h-64 overflow-auto pr-1">
                        {liens.map((l) => (
                          <div
                            key={l.id}
                            className="rounded-lg border border-gray-200 dark:border-gray-800 p-3 text-sm"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-semibold text-gray-900 dark:text-white">
                                {l.lien_type || "—"}
                              </div>
                              <Badge
                                color={
                                  l.status === "OPEN"
                                    ? "failure"
                                    : l.status === "RELEASED"
                                      ? "success"
                                      : "warning"
                                }
                              >
                                {l.status || "UNKNOWN"}
                              </Badge>
                            </div>

                            <div className="mt-1 text-gray-700 dark:text-gray-200">
                              <span className="text-gray-500">Amount:</span>{" "}
                              {money(l.amount)}
                              {" • "}
                              <span className="text-gray-500">
                                Recorded:
                              </span>{" "}
                              {l.recorded_at || "—"}
                            </div>

                            {l.source_url ? (
                              <a
                                href={l.source_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-blue-600 dark:text-blue-400 break-all"
                              >
                                {l.source_url}
                              </a>
                            ) : null}

                            {l.notes ? (
                              <div className="mt-2 text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                                {l.notes}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                      Nenhum lien cadastrado.
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    VA - Nota e Comentários
                  </div>

                  <div className="mt-3 flex items-center gap-2">
                    {ratingBadge(property.rating)}
                  </div>

                  <div className="mt-3 text-sm">
                    <div className="text-gray-500">Comentários</div>
                    <div className="whitespace-pre-wrap text-gray-900 dark:text-white">
                      {property.va_comments || "—"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Legal desc */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900">
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                Legal Description
              </div>
              <div className="mt-2 text-sm whitespace-pre-wrap text-gray-900 dark:text-white">
                {property.legal_description || "—"}
              </div>
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="gray" onClick={onClose}>
          Fechar
        </Button>
      </ModalFooter>
    </Modal>
  );
}

